import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, FileDown, ChevronRight, ClipboardCheck, Hammer, MessageSquareText,
  ListChecks, AlertTriangle, CheckCircle2, Activity
} from "lucide-react";

const KIND_LABEL = { template: "Template", run: "Audit Run", action: "Corrective Action" };

export default function TracePage() {
  const { kind, id } = useParams();
  const [bundle, setBundle] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const fetchIt = useCallback(async () => {
    try {
      const r = await api.get(`/trace/${kind}/${id}`);
      setBundle(r.data); setErrorMsg("");
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || "Failed to load trace");
    }
  }, [kind, id]);

  useEffect(() => { fetchIt(); }, [fetchIt]);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const r = await api.post(`/trace/${kind}/${id}/export-pdf`, {});
      const url = fileUrl(r.data.file_id);
      window.open(url, "_blank");
      toast.success("Trace bundle PDF generated · SHA-256 signed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setExporting(false); }
  };

  if (errorMsg) return (
    <div className="p-8">
      <div className="bg-[#fff1ea] border border-[#ff4500]/30 rounded-2xl p-6 max-w-xl" data-testid="trace-error">{errorMsg}</div>
    </div>
  );
  if (!bundle) return <div className="p-10 font-mono text-[#1a1a1a]/55 text-[11px] uppercase tracking-wider">Loading trace…</div>;

  const p = bundle.primary || {};
  const rec = p.record || {};
  const tpl = bundle.template;
  const run = bundle.run;
  const actions = bundle.actions || [];
  const chat = bundle.chat || [];
  const timeline = bundle.timeline || [];
  const primaryLabel = rec.title || rec.template_name || rec.name || "—";
  const primaryId = rec.human_id || rec.display_id || (rec.id || "").slice(0, 8);

  return (
    <div className="p-6 lg:p-8 space-y-5">
      {/* HERO */}
      <section className="hero-navy rounded-3xl p-6 lg:p-8 text-white overflow-hidden relative">
        <div className="absolute inset-0 dotted opacity-25" />
        <div className="absolute -top-20 -right-10 w-72 h-72 rounded-full bg-[#ff4500]/25 blur-3xl" />
        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-3 py-1 backdrop-blur-sm">
              <ShieldCheck className="w-3 h-3 text-[#ff5f15]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.18em]">Auditor view · trace bundle</span>
            </div>
            <h1 className="mt-3 font-display font-black text-3xl lg:text-4xl tracking-tight" data-testid="trace-heading">
              {KIND_LABEL[p.kind] || "Record"} <span className="text-[#ff5f15]">{primaryId}</span>
            </h1>
            <div className="mt-1 text-sm text-white/75 max-w-xl">{primaryLabel}</div>
          </div>
          <button data-testid="trace-export-pdf" onClick={exportPdf} disabled={exporting}
            className="btn-pill bg-[#ff4500] text-white hover:bg-[#cc3500] font-display font-black self-start lg:self-auto px-5 py-2.5 shadow-glow disabled:opacity-50">
            <FileDown className="w-3.5 h-3.5" /> {exporting ? "Generating…" : "Export signed PDF"}
          </button>
        </div>
      </section>

      {/* Chain of evidence */}
      <div className="surface-card rounded-2xl p-5" data-testid="trace-chain">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-2">// Chain of evidence</div>
        <div className="flex flex-wrap items-center gap-2">
          {tpl && (
            <ChainCard testId="chain-template" icon={ListChecks} label="Template" name={tpl.name} sub={`${(tpl.items || []).length} item(s)`} />
          )}
          {tpl && run && <ChevronRight className="w-4 h-4 text-[#1a1a1a]/30" />}
          {run && (
            <ChainCard testId="chain-run" icon={ClipboardCheck} label="Audit Run"
              name={run.template_name || "Run"} sub={`${run.display_id || "RUN"} · ${run.status}`}
              link={`/app/runs/${run.id}`} />
          )}
          {(run && actions.length > 0) && <ChevronRight className="w-4 h-4 text-[#1a1a1a]/30" />}
          {actions.map((a) => (
            <ChainCard key={a.id} testId={`chain-action-${a.id}`} icon={Hammer} label="Corrective Action"
              name={a.title} sub={`${a.human_id || a.display_id || ""} · ${a.status}`}
              link={`/app/actions/${a.id}`}
              tone={a.status === "completed" ? "#1a8754" : "#ff4500"} />
          ))}
        </div>
      </div>

      {/* Timeline */}
      <div className="surface-card rounded-2xl p-5" data-testid="trace-timeline">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-2">// Timeline ({timeline.length})</div>
        {timeline.length === 0 ? (
          <div className="text-[12px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">No events recorded yet.</div>
        ) : (
          <ol className="space-y-2 relative pl-5">
            <div className="absolute left-1.5 top-1 bottom-1 w-0.5 bg-[#1a1a1a]/10" />
            {timeline.map((ev, i) => (
              <li key={i} className="relative" data-testid={`timeline-event-${i}`}>
                <span className="absolute -left-5 top-1.5 w-2.5 h-2.5 rounded-full" style={{ background: dotColor(ev.kind) }} />
                <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">
                  {(ev.at || "").slice(0, 19).replace("T", " ")} · {ev.kind.replace("_", " ")}
                </div>
                <div className="font-display font-bold text-sm">{ev.label}</div>
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* Chat trail */}
      <div className="surface-card rounded-2xl p-5" data-testid="trace-chat">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-2">// Chat trail ({chat.length})</div>
        {chat.length === 0 ? (
          <div className="text-[12px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">
            No linked discussion yet. Use the <b>Discuss in hub</b> button on the Action or Audit Run page to start one.
          </div>
        ) : (
          <ul className="divide-y divide-[#1a1a1a]/08">
            {chat.map((m) => (
              <li key={m.id} className="py-2.5" data-testid={`trace-chat-${m.id}`}>
                <div className="text-[10px] font-mono uppercase tracking-wider text-[#ff4500] flex items-center gap-2 flex-wrap">
                  <span className="font-bold">{m.user_name}</span>
                  {m.user_title && <span className="text-[#1a1a1a]/55">· {m.user_title}</span>}
                  <span className="text-[#1a1a1a]/55">· {new Date(m.created_at).toLocaleString()}</span>
                  <span className="text-[#1a1a1a]/40">· signed {m.signature_name || m.user_name}</span>
                  <Link to="/app/teams" className="text-[#1a1a1a]/55 hover:text-[#ff4500] underline">view in hub →</Link>
                </div>
                <div className="text-sm leading-snug mt-1 whitespace-pre-wrap">{m.message}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ChainCard({ icon: Icon, label, name, sub, link, tone, testId }) {
  const inner = (
    <div data-testid={testId} className="bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 min-w-[180px] hover:border-[#ff4500]">
      <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#ff4500] flex items-center gap-1">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="font-display font-bold text-sm truncate" style={{ color: tone || "#1a1a1a" }}>{name}</div>
      {sub && <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">{sub}</div>}
    </div>
  );
  return link ? <Link to={link}>{inner}</Link> : inner;
}

function dotColor(kind) {
  if (kind === "chat") return "#1a1a1a";
  if (kind === "action_closed" || kind === "run_closed") return "#1a8754";
  if (kind === "action_created") return "#ff4500";
  if (kind === "run_started") return "#d6a900";
  return "#1a1a1a";
}
